import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  MapPin, Clock, CheckCircle, Truck, Wrench, AlertTriangle, Star, User, Shield
} from 'lucide-react';
import UnifiedMap from '../components/common/UnifiedMap';
import UserAvatar from '../components/common/UserAvatar';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const statusSteps = [
  { key: 'pending', label: 'Pending', icon: Clock, color: 'text-yellow-600' },
  { key: 'accepted', label: 'Accepted', icon: CheckCircle, color: 'text-blue-600' },
  { key: 'on_the_way', label: 'On the Way', icon: Truck, color: 'text-purple-600' },
  { key: 'arrived', label: 'Arrived', icon: MapPin, color: 'text-indigo-600' },
  { key: 'in_progress', label: 'In Progress', icon: Wrench, color: 'text-orange-600' },
  { key: 'completed', label: 'Completed', icon: CheckCircle, color: 'text-green-600' },
];

export default function PublicTracking() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/booking/${shareToken}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.status === 404 ? 'This tracking link is invalid or expired.' : 'Failed to load tracking info.');
    } finally {
      setIsLoading(false);
    }
  }, [shareToken]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const currentStep = statusSteps.findIndex(s => s.key === data.status);
  const isCompleted = data.status === 'completed';
  const isCancelled = data.status === 'cancelled';
  const hasMap = data.address?.latitude && data.address?.longitude;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="public-tracking">
      {/* Header */}
      <header className="bg-slate-900 py-4 px-4">
        <div className="max-w-lg mx-auto">
          <h1 className="text-white font-bold text-lg font-[Poppins]">HandyBayani</h1>
          <p className="text-slate-400 text-sm">Live Booking Tracker</p>
        </div>
      </header>

      <main className="px-4 py-6 max-w-lg mx-auto space-y-4">
        {/* Status Banner */}
        <div className={`rounded-2xl p-4 ${
          isCancelled ? 'bg-red-50 border border-red-200' :
          isCompleted ? 'bg-green-50 border border-green-200' :
          'bg-amber-50 border border-amber-200'
        }`} data-testid="status-banner">
          <div className="flex items-center gap-3 mb-3">
            {isCancelled ? <AlertTriangle className="w-5 h-5 text-red-600" /> :
             isCompleted ? <CheckCircle className="w-5 h-5 text-green-600" /> :
             <Clock className="w-5 h-5 text-amber-600" />}
            <span className="font-semibold text-sm">
              {data.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          </div>
          {!isCancelled && (
            <div className="flex items-center gap-1">
              {statusSteps.map((step, idx) => (
                <div key={step.key} className={`h-1.5 flex-1 rounded-full ${
                  idx <= currentStep ? 'bg-slate-900' : 'bg-stone-200'
                }`} />
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        {hasMap && (
          <UnifiedMap
            center={[data.address.latitude, data.address.longitude]}
            zoom={14}
            height="220px"
            markers={[
              { lat: data.address.latitude, lng: data.address.longitude, color: 'red', label: data.address.full_address },
              ...(data.provider_location ? [{ lat: data.provider_location.latitude, lng: data.provider_location.longitude, color: 'blue', label: 'Provider' }] : [])
            ]}
          />
        )}

        {/* Service & Provider Info */}
        <div className="bg-white rounded-2xl p-4 shadow-sm space-y-4">
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Service</p>
            <p className="font-medium text-slate-900">{data.service_name}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Schedule</p>
            <p className="text-sm text-slate-700">{data.scheduled_date} at {data.scheduled_time}</p>
          </div>
          {data.address?.full_address && (
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Location</p>
              <p className="text-sm text-slate-700">{data.address.full_address}</p>
            </div>
          )}
          {data.provider && (
            <div>
              <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Provider</p>
              <div className="flex items-center gap-3 mt-1">
                <UserAvatar src={data.provider.profile_image} name={data.provider.name} role="provider" size="md" />
                <span className="font-medium text-slate-900">{data.provider.name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Timeline */}
        {data.timeline?.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm" data-testid="public-timeline">
            <p className="text-xs text-slate-500 uppercase font-semibold mb-3">Status Updates</p>
            <div className="space-y-0">
              {data.timeline.map((entry, idx) => {
                const step = statusSteps.find(s => s.key === entry.status);
                const StepIcon = step?.icon || Clock;
                const isLast = idx === data.timeline.length - 1;
                return (
                  <div key={entry.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isLast ? 'bg-slate-900' : 'bg-stone-200'}`}>
                        <StepIcon className={`w-4 h-4 ${isLast ? 'text-white' : 'text-slate-500'}`} />
                      </div>
                      {idx < data.timeline.length - 1 && <div className="w-0.5 h-6 bg-stone-200" />}
                    </div>
                    <div className="pb-3">
                      <p className={`font-medium text-sm ${isLast ? 'text-slate-900' : 'text-slate-600'}`}>
                        {entry.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </p>
                      <p className="text-xs text-slate-400">{new Date(entry.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 pt-4">
          Powered by HandyBayani &mdash; Home Services Marketplace
        </p>
      </main>
    </div>
  );
}
